import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowRight, Plus, X, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<string>("");

  // Personal Info
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [headline, setHeadline] = useState("");
  const [location, setLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [targetRoles, setTargetRoles] = useState<string[]>([""]);
  // Work Experience
  const [experiences, setExperiences] = useState([{
    company: "",
    title: "",
    start_date: "",
    end_date: "",
    is_current: false,
    achievements: [""]
  }]);

  // Education
  const [education, setEducation] = useState([{
    institution: "",
    degree: "",
    field_of_study: "",
    start_year: "",
    end_year: ""
  }]);

  // Skills
  const [skills, setSkills] = useState<string[]>([""]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/login");
      return;
    }
    setUserId(user.id);

    // Load existing profile if available
    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (profile) {
      setFullName(profile.full_name || "");
      setEmail(profile.email || user.email || "");
      setHeadline(profile.headline || "");
      setLocation(profile.location || "");
      setPhone(profile.phone || "");
      setLinkedinUrl(profile.linkedin_url || "");
      setPortfolioUrl(profile.portfolio_url || "");
      setAvatarUrl(profile.avatar_url || "");
      setTargetRoles(profile.target_roles || [""]);
    } else {
      setEmail(user.email || "");
    }

    // Load existing work experiences
    const { data: workExps } = await supabase
      .from("work_experiences")
      .select("*")
      .eq("user_id", user.id)
      .order("start_date", { ascending: false });

    if (workExps && workExps.length > 0) {
      setExperiences(workExps.map(exp => ({
        company: exp.company,
        title: exp.title,
        start_date: exp.start_date || "",
        end_date: exp.end_date || "",
        is_current: exp.is_current || false,
        achievements: Array.isArray(exp.achievements) 
          ? exp.achievements.filter((a): a is string => typeof a === 'string')
          : [""]
      })));
    }

    // Load existing education
    const { data: educations } = await supabase
      .from("educations")
      .select("*")
      .eq("user_id", user.id)
      .order("start_year", { ascending: false });

    if (educations && educations.length > 0) {
      setEducation(educations.map(edu => ({
        institution: edu.institution,
        degree: edu.degree,
        field_of_study: edu.field_of_study || "",
        start_year: edu.start_year?.toString() || "",
        end_year: edu.end_year?.toString() || ""
      })));
    }

    // Load existing skills
    const { data: userSkills } = await supabase
      .from("skills")
      .select("*")
      .eq("user_id", user.id)
      .order("name", { ascending: true });

    if (userSkills && userSkills.length > 0) {
      setSkills(userSkills.map(skill => skill.name));
    }
  };

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length <= 3) return phoneNumber;
    if (phoneNumber.length <= 6) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setAvatarUrl(previewUrl);
    }
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarUrl("");
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !userId) return null;

    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;

      const { error: uploadError, data } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, {
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      toast.error('Failed to upload avatar');
      console.error(error);
      return null;
    }
  };



  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const addTargetRole = () => {
    setTargetRoles([...targetRoles, ""]);
  };

  const updateTargetRole = (index: number, value: string) => {
    const newRoles = [...targetRoles];
    newRoles[index] = value;
    setTargetRoles(newRoles);
  };

  const removeTargetRole = (index: number) => {
    setTargetRoles(targetRoles.filter((_, i) => i !== index));
  };

  const addExperience = () => {
    setExperiences([...experiences, {
      company: "",
      title: "",
      start_date: "",
      end_date: "",
      is_current: false,
      achievements: [""]
    }]);
  };

  const addAchievement = (expIndex: number) => {
    const newExperiences = [...experiences];
    newExperiences[expIndex].achievements.push("");
    setExperiences(newExperiences);
  };

  const updateAchievement = (expIndex: number, achIndex: number, value: string) => {
    const newExperiences = [...experiences];
    newExperiences[expIndex].achievements[achIndex] = value;
    setExperiences(newExperiences);
  };

  const addSkill = () => {
    setSkills([...skills, ""]);
  };

  const updateSkill = (index: number, value: string) => {
    const newSkills = [...skills];
    newSkills[index] = value;
    setSkills(newSkills);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Upload avatar if a new file was selected
      let finalAvatarUrl = avatarUrl;
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar();
        if (uploadedUrl) {
          finalAvatarUrl = uploadedUrl;
        }
      }

      // Update profile
      await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          email: email,
          headline,
          location,
          phone,
          linkedin_url: linkedinUrl,
          portfolio_url: portfolioUrl,
          avatar_url: finalAvatarUrl,
          target_roles: targetRoles.filter(r => r.trim() !== "")
        })
        .eq("id", userId);

      // Delete existing work experiences, education, and skills to prevent duplicates
      await supabase.from("work_experiences").delete().eq("user_id", userId);
      await supabase.from("educations").delete().eq("user_id", userId);
      await supabase.from("skills").delete().eq("user_id", userId);

      // Save work experiences
      for (const exp of experiences) {
        if (exp.company && exp.title) {
          await supabase
            .from("work_experiences")
            .insert({
              user_id: userId,
              company: exp.company,
              title: exp.title,
              start_date: exp.start_date || null,
              end_date: exp.end_date || null,
              is_current: exp.is_current,
              achievements: exp.achievements.filter(a => a.trim() !== "")
            });
        }
      }

      // Save education
      for (const edu of education) {
        if (edu.institution && edu.degree) {
          await supabase
            .from("educations")
            .insert({
              user_id: userId,
              ...edu,
              start_year: edu.start_year ? parseInt(edu.start_year) : null,
              end_year: edu.end_year ? parseInt(edu.end_year) : null
            });
        }
      }

      // Save skills
      for (const skill of skills) {
        if (skill.trim() !== "") {
          await supabase
            .from("skills")
            .insert({
              user_id: userId,
              name: skill,
              level: 3
            });
        }
      }

      toast.success("Profile saved! Ready to generate your resume.");
      navigate("/app/resume");
    } catch (error: any) {
      toast.error("Failed to save profile");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Logo className="h-16 w-16" />
              <span className="text-xl font-bold">LoopBaan.ai</span>
            </div>
            <Link to="/app">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto max-w-3xl px-4 py-12">
        {/* Progress */}
        <div className="mb-8 flex items-center justify-between">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                s <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                {s}
              </div>
              {s < 4 && <div className={`mx-2 h-1 w-16 ${s < step ? "bg-primary" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Personal Info */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Tell us about yourself to get started</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john.doe@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="headline">Professional Headline *</Label>
                <Input
                  id="headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Senior Software Engineer | Full-Stack Developer"
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                    placeholder="555-123-4567"
                    maxLength={12}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn URL</Label>
                <Input
                  id="linkedin"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/johndoe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="portfolio">Portfolio URL</Label>
                <Input
                  id="portfolio"
                  value={portfolioUrl}
                  onChange={(e) => setPortfolioUrl(e.target.value)}
                  placeholder="https://johndoe.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="avatar">Profile Picture</Label>
                {!avatarUrl ? (
                  <Input
                    id="avatar"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                  />
                ) : (
                  <div className="flex items-center gap-4">
                    <img 
                      src={avatarUrl} 
                      alt="Avatar preview" 
                      className="w-24 h-24 rounded-full object-cover border-2 border-primary"
                    />
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          removeAvatar();
                          const input = document.getElementById('avatar') as HTMLInputElement;
                          if (input) input.value = '';
                        }}
                      >
                        <X className="mr-2 h-4 w-4" /> Remove
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('avatar-change')?.click()}
                      >
                        Change Photo
                      </Button>
                      <Input
                        id="avatar-change"
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Upload a professional headshot to make your resume stand out</p>
              </div>
              <div className="space-y-2">
                <Label>Target Roles</Label>
                {targetRoles.map((role, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={role}
                      onChange={(e) => updateTargetRole(i, e.target.value)}
                      placeholder="Software Engineer"
                    />
                    {targetRoles.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeTargetRole(i)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addTargetRole}>
                  <Plus className="mr-2 h-4 w-4" /> Add Role
                </Button>
              </div>
            </CardContent>
            <div className="flex justify-end p-6">
              <Button onClick={handleNext} disabled={!fullName || !headline}>
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Work Experience */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Work Experience</CardTitle>
              <CardDescription>Share your professional background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {experiences.map((exp, expIndex) => (
                <div key={expIndex} className="space-y-4 rounded-lg border p-4">
                  <Badge variant="outline">Experience {expIndex + 1}</Badge>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Company *</Label>
                      <Input
                        value={exp.company}
                        onChange={(e) => {
                          const newExp = [...experiences];
                          newExp[expIndex].company = e.target.value;
                          setExperiences(newExp);
                        }}
                        placeholder="TechCorp Inc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={exp.title}
                        onChange={(e) => {
                          const newExp = [...experiences];
                          newExp[expIndex].title = e.target.value;
                          setExperiences(newExp);
                        }}
                        placeholder="Senior Software Engineer"
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={exp.start_date}
                        onChange={(e) => {
                          const newExp = [...experiences];
                          newExp[expIndex].start_date = e.target.value;
                          setExperiences(newExp);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={exp.end_date}
                        onChange={(e) => {
                          const newExp = [...experiences];
                          newExp[expIndex].end_date = e.target.value;
                          setExperiences(newExp);
                        }}
                        disabled={exp.is_current}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`current-${expIndex}`}
                      checked={exp.is_current}
                      onCheckedChange={(checked) => {
                        const newExp = [...experiences];
                        newExp[expIndex].is_current = checked === true;
                        if (checked) {
                          newExp[expIndex].end_date = "";
                        }
                        setExperiences(newExp);
                      }}
                    />
                    <Label 
                      htmlFor={`current-${expIndex}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      Currently employed here
                    </Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Key Achievements (use STAR method: Situation, Task, Action, Result)</Label>
                    {exp.achievements.map((achievement, achIndex) => (
                      <Textarea
                        key={achIndex}
                        value={achievement}
                        onChange={(e) => updateAchievement(expIndex, achIndex, e.target.value)}
                        placeholder="Increased revenue by 40% by implementing..."
                        rows={2}
                      />
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addAchievement(expIndex)}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Achievement
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addExperience}>
                <Plus className="mr-2 h-4 w-4" /> Add Experience
              </Button>
            </CardContent>
            <div className="flex justify-between p-6">
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button onClick={handleNext}>
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 3: Education */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Education</CardTitle>
              <CardDescription>Share your educational background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {education.map((edu, index) => (
                <div key={index} className="space-y-4 rounded-lg border p-4">
                  <Badge variant="outline">Education {index + 1}</Badge>
                  <div className="space-y-2">
                    <Label>Institution *</Label>
                    <Input
                      value={edu.institution}
                      onChange={(e) => {
                        const newEdu = [...education];
                        newEdu[index].institution = e.target.value;
                        setEducation(newEdu);
                      }}
                      placeholder="University of California"
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Degree *</Label>
                      <Input
                        value={edu.degree}
                        onChange={(e) => {
                          const newEdu = [...education];
                          newEdu[index].degree = e.target.value;
                          setEducation(newEdu);
                        }}
                        placeholder="Bachelor of Science"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Field of Study</Label>
                      <Input
                        value={edu.field_of_study}
                        onChange={(e) => {
                          const newEdu = [...education];
                          newEdu[index].field_of_study = e.target.value;
                          setEducation(newEdu);
                        }}
                        placeholder="Computer Science"
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Start Year</Label>
                      <Input
                        type="number"
                        value={edu.start_year}
                        onChange={(e) => {
                          const newEdu = [...education];
                          newEdu[index].start_year = e.target.value;
                          setEducation(newEdu);
                        }}
                        placeholder="2015"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Year</Label>
                      <Input
                        type="number"
                        value={edu.end_year}
                        onChange={(e) => {
                          const newEdu = [...education];
                          newEdu[index].end_year = e.target.value;
                          setEducation(newEdu);
                        }}
                        placeholder="2019"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
            <div className="flex justify-between p-6">
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button onClick={handleNext}>
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 4: Skills */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Skills</CardTitle>
              <CardDescription>List your key technical and soft skills</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {skills.map((skill, index) => (
                <Input
                  key={index}
                  value={skill}
                  onChange={(e) => updateSkill(index, e.target.value)}
                  placeholder="React, Node.js, Leadership, etc."
                />
              ))}
              <Button type="button" variant="outline" onClick={addSkill}>
                <Plus className="mr-2 h-4 w-4" /> Add Skill
              </Button>
            </CardContent>
            <div className="flex justify-between p-6">
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? "Saving..." : "Complete & Generate Resume"}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Onboarding;